// Backend · Alerts: the small pieces every tab and drawer shares.

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { QueryKey } from '@tanstack/react-query';
import { RequiredField } from '@/components/required-field';
import { Icons } from '@/components/primitives';
import { queryClient } from '@/cache/queryClient';
import type { AlertMetricCatalogEntry } from '@/api/types';

export function invalidate(...keys: QueryKey[]): void {
  keys.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
}

export function Badge({ children, color }: { children: ReactNode; color: string }) {
  return <span className="alert-severity-badge" style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, color: 'var(--ink-on-badge)', background: color }}>{children}</span>;
}

export interface MetricExplainerHintProps {
  entry?: Pick<AlertMetricCatalogEntry, 'summary' | 'calculation' | 'example'> | null;
}

/// Catalog-driven explainer under the editor's metric pickers: what the
/// selected metric measures, how it is computed, and a worked example — the
/// same server copy the drawers show, so the two can never disagree.
export function MetricExplainerHint({ entry }: MetricExplainerHintProps) {
  if (!entry?.summary) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="field-hint" style={{ marginBottom: 2 }}>{entry.summary}</div>
      <div className="field-hint" style={{ marginBottom: 2 }}>{entry.calculation}</div>
      {entry.example && entry.example !== '—' && (
        <div className="field-hint"><span style={{ fontWeight: 600 }}>Example: </span>{entry.example}</div>
      )}
    </div>
  );
}

export interface FieldProps {
  label: string;
  children: ReactNode;
  /**
   * The `id` of the control this label names. Give it whenever the child is a
   * native input or select: an unassociated label leaves the control nameless
   * (axe select-name / label). A child that names itself (a radio group with
   * its own `aria-label`) needs none.
   */
  controlId?: string;
  fieldKey?: string;
  message?: string | null;
  attempt?: number;
}

export function Field({ label, children, controlId, fieldKey, message, attempt = 0 }: FieldProps) {
  const content = (
    <>
      <label className="field-lbl" htmlFor={controlId}><span>{label}</span></label>
      {children}
    </>
  );
  return fieldKey ? (
    <RequiredField
      fieldKey={fieldKey}
      message={message}
      attempt={attempt}
      style={{ marginBottom: 10 }}
    >
      {content}
    </RequiredField>
  ) : (
    <div style={{ marginBottom: 10 }}>{content}</div>
  );
}

export function Row2({ children }: { children: ReactNode }) {
  return <div className="stack-phone" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>{children}</div>;
}

export interface DeleteWithConfirmProps {
  /** The pop-over's question, e.g. "Delete this rule?". */
  title: string;
  /** What deleting means, under the question. */
  body: ReactNode;
  busy?: boolean;
  onConfirm: () => void;
}

/**
 * A drawer footer's Delete button that asks first, in the inline pop-over
 * Admin → Users uses for Disable and Delete (`.ad-confirm-pop`): nothing is
 * deleted until the pop-over's own Delete is pressed. Cancel, a click outside
 * or a second press of the trigger closes it. Render it inside a footer that
 * is `position: relative`, as its first control, so the arrow points at it.
 */
export function DeleteWithConfirm({ title, body, busy = false, onConfirm }: DeleteWithConfirmProps) {
  const [open, setOpen] = useState(false);
  const popId = useId();
  const titleId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    // Keyboard users land on the safe choice.
    cancelRef.current?.focus();
    const outside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (popRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-sm ad-confirm-trigger"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        disabled={busy}
        onClick={() => setOpen((current) => !current)}
      >
        {Icons.x} Delete
      </button>
      {open && (
        <div ref={popRef} id={popId} className="ad-confirm-pop" role="group" aria-labelledby={titleId} style={{ left: 12, maxWidth: 'calc(100% - 24px)' }}>
          <div className="ad-confirm-arrow"/>
          <div id={titleId} className="ad-confirm-title">{title}</div>
          <div className="ad-confirm-body">{body}</div>
          <div className="ad-confirm-actions">
            <button
              ref={cancelRef}
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => { setOpen(false); triggerRef.current?.focus(); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm ad-confirm-go"
              disabled={busy}
              onClick={() => { setOpen(false); onConfirm(); }}
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The channel pop-over's second sentence: the rules that deliver through it,
 * or that none do. `null` while the rules are not loaded, so it never claims
 * "No rules use it." about a list it has not read.
 */
export function channelUsageSentence(
  channelId: string,
  rules: readonly { name: string; channel_ids: readonly string[] }[] | undefined,
): string | null {
  if (!rules) return null;
  const names = rules.filter((rule) => rule.channel_ids.includes(channelId)).map((rule) => rule.name);
  return names.length ? `Rules using it: ${names.join(', ')}.` : 'No rules use it.';
}
